// HILDR — ildåpningsbestemmelser etter GRUS (Grunnleggende soldatferdigheter,
// Heimevernet, HVSKS v1.0 des. 2018, s. 53; kilde der: Reglementet for fotlaget).
//
// HVORFOR DETTE ER KODE OG IKKE FRITEKST: GRUS fastsetter én regel som er en
// HARD invariant, og som programvare derfor skal håndheve framfor å håpe på:
//
//   «Den enkelte underavdeling kan velge å gi strengere ildåpningsbestemmelser
//    enn det som er gitt ham. Men man kan ikke gi mildere.»
//
// Eksempelet i GRUS: troppssjef kan gi sine lagførere «ildåpning fri», men LF kan
// velge å gi sine soldater «på ordre». Motsatt vei er ULOVLIG. Et C2-system som
// lar en underordnet løsne på ildåpningen har innført en reell fare, ikke en bug.
//
// Og: «Uavhengig av ildåpningsbestemmelser har enhver soldat rett til å åpne ild
// i selvforsvar.» Retten til selvforsvar kan derfor ALDRI slås av — heller ikke
// under ildforbud, der GRUS sier eksplisitt at «ild i selvforsvar vil alltid være
// tillatt».
import { stridsEmphasis } from '@skade/contracts';
/**
 * Strenghetsrangering. Høyere tall = strengere. Rangeringen ER regelen om at
 * man kan stramme inn men ikke løsne, og den er derfor testet mot GRUS-eksempelet
 * (tropp gir «fri», LF kan gi «på ordre», men ikke omvendt).
 *
 * «Fri» er mildest fordi tidsvalget overlates til mottakende nivå; «ildforbud»
 * er strengest fordi all ild skal stanses.
 */
export const ILD_STRENGHET = {
    fri: 0,
    ildapningslinje: 1,
    avstand: 2,
    paaKommando: 3,
    paaOrdre: 4,
    ildforbud: 5,
};
export const ILDAPNING = {
    fri: {
        type: 'fri', navn: 'Ildåpning fri',
        betydning: 'Valg av tid for ildåpning overlates til det nivået som mottar ordren, '
            + 'innenfor gjeldende RoE.',
        bruk: 'Uoversiktlige situasjoner, store avstander, mørke og dårlig sikt.',
    },
    ildapningslinje: {
        type: 'ildapningslinje', navn: 'Ildåpningslinje',
        betydning: 'En linje i lendet som markerer overgang fra «på ordre» til «på observasjon». '
            + 'Ild kan åpnes så snart fienden passerer linjen.',
        bruk: 'Kraftsamling i tid og rom, og for å sikre ildåpning ved bortfall av ledelsespunkt '
            + 'eller kommunikasjon. Kraftlinjer, bekker, veier, høydedrag og skogkanter kan brukes.',
        krever: 'linje',
    },
    avstand: {
        type: 'avstand', navn: 'Ildåpningsavstand',
        betydning: 'Ild kan tidligst åpnes når fienden er innenfor en gitt avstand.',
        bruk: 'Angrep i mørke der man vil manøvrere nærmere før ild åpnes; i forsvar når '
            + 'ildåpningslinje er vanskelig å ta ut, eller man vil slippe fienden nærmere for å '
            + 'utnytte flere av avdelingens våpen.',
        krever: 'avstandM',
    },
    paaKommando: {
        type: 'paaKommando', navn: 'På kommando',
        betydning: 'Ilden utløses samlet i tid og rom på et helt klart signal.',
        bruk: 'Overraskelse og kraftsamling. Signal med enkelhet som prinsipp: lyssignal, '
            + 'sporlys, første skudd, første PV-våpen, nedtelling på nett.',
    },
    paaOrdre: {
        type: 'paaOrdre', navn: 'På ordre',
        betydning: 'Ild kan kun åpnes når ordregiver har gitt klarsignal.',
        bruk: 'Sjokkeffekt, skjule grupperingen lengst mulig, og der avdelingssjefen er usikker '
            + 'på situasjonen.',
    },
    ildforbud: {
        type: 'ildforbud', navn: 'Ildforbud (bundet)',
        betydning: 'All ild skal stanses. Kan begrenses i tid og rom. '
            + 'Ild i selvforsvar er alltid tillatt.',
        bruk: 'Veldig uoversiktlige situasjoner, eller der man forventer egne inn i ildområdet.',
    },
};
/** Alle typer, mildest først — rekkefølgen et UI skal tilby dem i. */
export const ILDAPNING_VALG = Object.keys(ILD_STRENGHET)
    .sort((a, b) => ILD_STRENGHET[a] - ILD_STRENGHET[b]);
/**
 * RETTEN TIL SELVFORSVAR. Egen konstant fordi den ikke er en bestemmelse man
 * kan velge bort: GRUS er eksplisitt om at den gjelder uavhengig av
 * ildåpningsbestemmelsene, også under ildforbud.
 */
export const SELVFORSVAR_ALLTID = 'Uavhengig av ildåpningsbestemmelser har enhver soldat rett til å åpne ild i selvforsvar.';
/** Er bestemmelsen komplett utfylt? Manglende avstand/linje gjør den ubrukelig i felt. */
export function manglerVedBestemmelse(b) {
    const m = [];
    const def = ILDAPNING[b.type];
    if (def.krever === 'avstandM' && !(typeof b.avstandM === 'number' && b.avstandM > 0)) {
        m.push('Ildåpningsavstand i meter mangler');
    }
    if (def.krever === 'linje' && !b.linje?.trim()) {
        m.push('Ildåpningslinje i lendet mangler (kraftlinje, bekk, vei, høydedrag, skogkant)');
    }
    return m;
}
/**
 * Kan et nivå gi videre `til` når det selv har fått `fra`?
 * GRUS: strengere er lov, mildere er IKKE lov. Uendret er selvsagt lov.
 */
export function kanGiVidere(fra, til) {
    const f = ILD_STRENGHET[fra];
    const t = ILD_STRENGHET[til];
    if (t >= f) {
        return {
            lovlig: true,
            begrunnelse: t === f
                ? `Uendret: «${ILDAPNING[til].navn}» videreføres.`
                : `Lovlig innstramming: «${ILDAPNING[fra].navn}» → «${ILDAPNING[til].navn}».`,
        };
    }
    return {
        lovlig: false,
        begrunnelse: `ULOVLIG: kan ikke gi mildere ildåpningsbestemmelse enn mottatt. `
            + `Du har «${ILDAPNING[fra].navn}» og forsøker å gi «${ILDAPNING[til].navn}». `
            + 'En underavdeling kan stramme inn, men aldri løsne (GRUS s. 53).',
    };
}
/** Den strengeste av to bestemmelser — brukes når flere føringer møtes. */
export function strengeste(a, b) {
    return ILD_STRENGHET[a] >= ILD_STRENGHET[b] ? a : b;
}
/**
 * Ildåpningsbestemmelsen som ordrelinje. «IKKE» og himmelretninger i STORE
 * bokstaver via stridsEmphasis, som resten av ordreverket.
 */
export function ildapningTilTekst(b) {
    const def = ILDAPNING[b.type];
    const deler = [`Ildåpning: ${def.navn.toUpperCase()}`];
    if (b.type === 'avstand' && b.avstandM)
        deler.push(`tidligst på ${b.avstandM} m`);
    if (b.type === 'ildapningslinje' && b.linje)
        deler.push(`linje: ${b.linje.trim()}`);
    if (b.type === 'paaKommando' && b.signal)
        deler.push(`signal: ${b.signal.trim()}`);
    if (b.merknad?.trim())
        deler.push(b.merknad.trim());
    // Retten til selvforsvar tas ALLTID med — den kan ikke velges bort.
    return stridsEmphasis(`${deler.join(' · ')}. ${SELVFORSVAR_ALLTID}`);
}
/**
 * Normalfordeling: hver soldat har ildsektor som tilsvarer sin plass i
 * formasjonen — høyre mann skyter på høyre fiende osv. GRUS: «Normalfordeling
 * gjelder alltid, hvis ingen annen ordre er gitt.»
 */
export const NORMALFORDELING = 'Normalfordeling: hver soldat har ildsektor som tilsvarer sin plass i formasjonen '
    + '(høyre mann → høyre mål). Gjelder alltid uten annen ordre.';
/** Hovedregel for målfordeling (GRUS s. 54). */
export const MALFORDELING_HOVEDREGEL = 'Bekjemp mål i EGEN sektor først, deretter i makkerens eller lagets ildområde.';
export function ildomradeTilTekst(o) {
    const d = [`Midten av målet: ${o.midtenAvMalet.trim()}`];
    if (o.venstreBegrensning?.trim())
        d.push(`venstre begrensning: ${o.venstreBegrensning.trim()}`);
    if (o.hoyreBegrensning?.trim())
        d.push(`høyre begrensning: ${o.hoyreBegrensning.trim()}`);
    return stridsEmphasis(`${d.join(' · ')}. ${NORMALFORDELING}`);
}
//# sourceMappingURL=ild.js.map